import {
  Component,
  Input,
  ApplicationRef,
  ChangeDetectorRef,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterContentChecked,
  OnInit,
  ComponentRef,
  OnDestroy,
} from '@angular/core';
import { NbDialogService, NbPopoverDirective } from '@nebular/theme';
import { Subject } from 'rxjs/Subject';

import { BaseComponentCanDeactivate } from 'app/@auth/guards/changes.guard';
import { isObservable } from 'rxjs';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  FitnessBlock,
  FitnessItem,
  BlockType,
  SetsBased,
  WeightType,
} from 'app/@core/interfaces/common/CalorieFriend/fitnessItem';
import { FitnessItemPresentationComponent } from '../FitnessItemPresentation/fitness-item-presentation.component';
import { FitnessItemPresentationComponentBase } from '../FitnessItemPresentation/fitness-item-presentation-base.component';
import { Exercise } from 'app/@core/interfaces/common/CalorieFriend/exercise';
import { AddFitnessItemsComponent } from '../AddFitnessItems/add-fitness-items.component';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { NativeAppService } from 'app/@core/backend/common/services/native-app.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ExerciseEditComponent } from '../ExerciseEdit/exercise-edit.component';
import { takeUntil } from 'rxjs/operators';

/**
 * Base Component class for Food Items List presentation in one Meal Set.
 */
export class FitnessItemsListPresentationComponentBase
  extends BaseComponentCanDeactivate
  implements OnInit, AfterContentChecked, OnDestroy {
  get IsDirty() {
    let childrenDirty = false;
    this.MealItemsList.forEach(
      (c) => (childrenDirty = childrenDirty || c.IsDirty)
    );
    return this.isDirty || childrenDirty;
  }

  resetDirty() {
    super.resetDirty();
    this.MealItemsList.forEach((c) => c.resetDirty());
  }

  @Input('FitnessBlock') protected FitnessBlock: FitnessBlock;
  @Input('BlockUpdate') BlockUpdate = null;
  @Input('PlanView') PlanView: boolean = false;

  @Input('BlockType') BlockType: BlockType;
  @Input('CircuitRest') CircuitRest: number = 0;

  @Input('PatientID') PatientID: number = -1;
  @Input('PatientName') PatientName: string = '';
  @Input('IsPreferredUnitImperial') IsPreferredUnitImperial: boolean = false;

  @ViewChild('scrollContainer') scrollContainer: any;
  @ViewChildren('FitnessItem')
  private MealItemsList: QueryList<FitnessItemPresentationComponentBase>;

  EmptyFitnessItem: FitnessItem = null;

  selectedItem: FitnessItem = null;
  DeleteIcon = 'trash';
  AddIcon = 'plus-circle-outline';

  PlanItems: Array<FitnessItem> = null;

  CurrentX = -1;
  CurrentY = -1;
  EditingItem = false;
  Editing = false;
  Deleting = false;
  AllowAddtingItems = false;

  DeleteMealItemConfirmationMessage = $localize`:@@DeleteMealItemConfirmationMessage:Are you sure you want to remove this item?`;

  AddItem = null;
  
  unsubscription$ = new Subject();

  // to-do: overlay component for defining sets and reps
  // OverlayComponent = OverlayMealItemPresentationComponent;

  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective;
  @ViewChild('ItemsContainer') ItemsContainer: any = null;
  @ViewChild('rowLayout') rowLayout: ElementRef = null;
  @ViewChildren(FitnessItemPresentationComponent)
  LoadedItems: QueryList<FitnessItemPresentationComponent>;

  ItemUpdateNotification: Subject<Object> = null;
  MaxRowItems = 0;

  itemsTable: Array<FitnessItem[]>;

  /** MealItemsPresentation ctor */
  constructor(
    private dialogService: NbDialogService,
    appRef: ApplicationRef,
    chdetRef: ChangeDetectorRef,
    AllowAddingItems = false,
    private deviceService: DeviceDetectorService
  ) {
    super(dialogService);
    this.ItemUpdateNotification = new Subject<Object>();
    this.AllowAddtingItems = AllowAddingItems;
  }

  cdkDelay() {
    return this.deviceService.isDesktop() ? 0 : 100;
  }

  ngOnInit(): void {
    setTimeout(() => {
      if (this.FitnessBlock !== null && this.FitnessBlock !== undefined) {
        this.PlanItems = this.FitnessBlock.items;
      }

      if (this.PlanItems == null || this.PlanItems === undefined)
        this.PlanItems = new Array<FitnessItem>();

      // sorting
      this.PlanItems = this.PlanItems.sort((a, b) =>
        a.order > b.order ? 1 : -1
      );

      if (
        this.FitnessBlock.items == null ||
        this.FitnessBlock.items === undefined
      )
        this.FitnessBlock.items = new Array<FitnessItem>();

      this.SetMaxItemsPerRow();

      for (const item of this.PlanItems) {
        this.SepcificItemSubscribe(item);
      }
    });

    // this.ItemSubscribe();
  }

  ngAfterContentChecked() { }

  ngOnDestroy() {
    this.unsubscription$.next();
    this.unsubscription$.complete();
  }

  onResize($event) { }

  private SetMaxItemsPerRow() {
    this.initTable();
  }

  getCumIndex(x: number, y: number) {
    let count: number = 0;
    const arr = this.getItemsTable();
    for (let i = 0; i < arr.length; i++) {
      if (x === i) {
        return count + y;
      } else {
        count += arr[i].length;
      }
    }
  }

  getItemsTable(): FitnessItem[][] {
    if (!this.rowLayout) return [];
    const oneMealWidth = 230;
    const width = this.rowLayout.nativeElement.clientWidth;
    const columnSize = Math.round(width / oneMealWidth);
    // view has been resized? => update table with new column size
    if (columnSize !== this.MaxRowItems) {
      this.MaxRowItems = columnSize;
      this.initTable();
    }
    return this.itemsTable;
  }

  initTable() {
    if (!this.PlanItems) return;

    let order = 0;
    for (const item of this.PlanItems) {
      item.order = order++;
    }

    // create table rows based on input list
    // example: [1,2,3,4,5,6] => [ [1,2,3], [4,5,6] ]
    const planItemWithEmpty = [...this.PlanItems, this.EmptyFitnessItem];
    this.itemsTable = planItemWithEmpty
      .filter((_, outerIndex) => outerIndex % this.MaxRowItems === 0) // create outter list of rows
      .map(
        (
          _,
          rowIndex // fill each row from...
        ) =>
          planItemWithEmpty.slice(
            rowIndex * this.MaxRowItems, // ... row start and
            rowIndex * this.MaxRowItems + this.MaxRowItems // ...row end
          )
      );
  }

  reorderDroppedItem(event: CdkDragDrop<number[]>) {
    // same row/container? => move item in same row
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      // different rows? => transfer item from one to another list
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // update items after drop: flatten matrix into list
    // example: [ [1,2,3], [4,5,6] ] => [1,2,3,4,5,6]
    this.PlanItems = this.itemsTable.reduce(
      (previous, current) => previous.concat(current),
      []
    );

    this.PlanItems = this.PlanItems.filter((item) => item != null);

    // re-initialize table - makes sure each row has same numbers of entries
    // example: [ [1,2], [3,4,5,6] ] => [ [1,2,3], [4,5,6] ]
    this.initTable();
  }

  SaveItem(item) {
    if (item) {
      item.Update.next();
    }
  }

  /**
   * Delete the selected meal item.
   */
  DeleteItem(item) {
    this.selectedItem = item;

    if (
      this.FitnessBlock !== null &&
      this.FitnessBlock !== undefined &&
      this.selectedItem !== null &&
      this.selectedItem !== undefined
    ) {
      const selectedItem = this.selectedItem;
      const dlg = this.dialogService
        .open(ConfirmMessageDialogComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            Message: $localize`:@@DeleteMealItemConfirmationMessage:Are you sure you want to remove this item?`,
          },
        })
        .onClose.subscribe((result) => {
          if (result) {
            this.PlanItems = this.FitnessBlock.items =
              this.FitnessBlock.items.filter((x) => x !== selectedItem);
            this.SetMaxItemsPerRow();
            this.ItemUpdateNotification.next();
          }
        });
    }

    this.selectedItem = null;
    this.isDirty = true;
  }

  /**
   * Method to open the MealItemsComponent component in a pop up window
   * to select meal items.
   */
  SelectItems() {

    const ActiveExercise = new Array<Exercise>();

    if (
      this.FitnessBlock !== null &&
      this.FitnessBlock !== undefined &&
      this.FitnessBlock.items !== null &&
      this.FitnessBlock.items !== undefined
    ) {
      for (const fitnessItem of this.FitnessBlock.items) {
        ActiveExercise.push(fitnessItem);
      }
    }

    this.dialogService.open(AddFitnessItemsComponent, {
      closeOnEsc: false,
      // context: { LoadedCurrentlyActiveFood: ActiveFood }
      context: {
        LoadedCurrentlyActiveExercise: [],
        CallerParent: this,
        PatientID: this.PatientID,
        PatientName: this.PatientName,
        IsPreferredUnitImperial: this.IsPreferredUnitImperial,
      },
    });
  }

  /**
   * Method to update the meals list with the items added.
   * @param items Array of MealItem objects
   */
  public UpdateSelectedItem(items) {
    if (items !== null && items !== undefined) {
      let HoldItems = null;

      if (this.FitnessBlock && this.FitnessBlock.items)
        HoldItems = this.FitnessBlock.items;

      if (items !== null && items !== undefined) {
        for (const item of items) {
          item.id = null;
          item.fitnessBlockID = this.FitnessBlock.id;
          this.FitnessBlock.items.push(item);

          this.SepcificItemSubscribe(item);
          this.ItemUpdateNotification.next();
        }
        this.isDirty = true;
      }

      this.PlanItems = this.FitnessBlock.items;

      this.SetMaxItemsPerRow();
    }
  }


  makeRandom() {
    let possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890";
    const lengthOfCode = 8;
    let text = "";
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }


  CreateAndAddQuickItem() {
    let item: FitnessItem = {
      name: 'Untiled Quick Add Item',
      isQuickAdd: true,
      Update: null,
      fitnessBlockID: this.FitnessBlock.id,
      sets: 3,
      setsBased: SetsBased.RepsBased,
      defaultRepsOrTime: 8,
      defaultTempo: '',
      defaultRest: 60,
      weightType: WeightType.Procent1MR,
      defaultWeight: 0,
      oneRepMax: 0,
      restBeforeNextExercise: 0,
      setSettings: '[{"Set":"1","Tempo":"","RepsOrTime":"8","Weight":"","Rest":"60"},{"Set":"2","Tempo":"","RepsOrTime":"8","Weight":"","Rest":"60"},{"Set":"3","Tempo":"","RepsOrTime":"8","Weight":"","Rest":"60"}]',
      order: 0,
      useFemaleVersion: false,
      showAdvancedMode: false,
      instructions: '',
      id: 0,
      exerciceId: this.makeRandom(),
      searchWeight: 0,
      videoName: '',
      imageName: '',
      imageNameFemale: '',
      equipments: [],
      bodyParts: [],
      muscleTarget: [],
      muscleSynergist: [],
      searchTerms: [],
      imageUrl: '',
      imageMediumUrl: '',
      femaleImageUrl: '',
      femaleImageMediumUrl: '',
      videoUrl: '',
      femaleVideoUrl: '',
      youtubeUrl: '',
      selected: false,
      isCustom: true,
      customName: 'Untiled Quick Add Item'
    }


    item.id = null;
    item.fitnessBlockID = this.FitnessBlock.id;
    this.FitnessBlock.items.push(item);

    this.SepcificItemSubscribe(item);
    this.ItemUpdateNotification.next();

    this.isDirty = true;


    this.PlanItems = this.FitnessBlock.items;

    this.SetMaxItemsPerRow();

    const ref: ComponentRef<ExerciseEditComponent> =
      this.dialogService.open(ExerciseEditComponent, {
        closeOnEsc: false,
        context: {
          InputItem: item,
          PatientID: this.PatientID,
          PatientName: this.PatientName,
          IsPreferredUnitImperial: this.IsPreferredUnitImperial,

          ShowEditButtons: true,
          RemoveCallBack: this.DeleteItem.bind(this,item),
          SaveCallBack: this.SaveItem.bind(this,item),
        },
      }).componentRef;



  }

  private RefeshList() {
    const hold = this.FitnessBlock.items;
    this.FitnessBlock.items = null;
    this.FitnessBlock.items = hold;
    return;
  }

  private ItemSubscribe() {
    if (this.FitnessBlock != null && this.FitnessBlock !== undefined) {
      for (const item of this.FitnessBlock.items) {
        this.SepcificItemSubscribe(item);
      }
    }
  }

  /**
   * Method to subscribe the change of specific meal item.
   * @param item MealItem object
   */
  private SepcificItemSubscribe(item) {
    if (!isObservable(item.Update)) item.Update = new Subject();

    item.Update
      .pipe(takeUntil(this.unsubscription$))
      .subscribe((data) => {
        this.RefeshList();
        this.ItemUpdateNotification.next();
    });
  }
}

/**
 * Component for MealItems Presentation.
 * Inherits from the MealItemsPresentationComponentBase component.
 */
@Component({
  selector: 'app-fitness-items-list-presentation',
  templateUrl: './fitness-items-list-presentation.component.html',
  styleUrls: [
    './fitness-items-list-presentation.component.scss',
    '../../global.component.scss',
  ],
})
export class FitnessItemsListPresentationComponent extends FitnessItemsListPresentationComponentBase {
  constructor(
    PCDialogService: NbDialogService,
    PCAppRef: ApplicationRef,
    PCChdetRef: ChangeDetectorRef,
    deviceService: DeviceDetectorService
  ) {
    super(PCDialogService, PCAppRef, PCChdetRef, true, deviceService);
  }
}

/**
 * Component for MealItems Presentation.
 * Inherits from the MealItemsPresentationComponentBase component.
 */
@Component({
  selector: 'app-fitness-items-list-presentation-mobile',
  templateUrl: './fitness-items-list-presentation-mobile.component.html',
  styleUrls: [
    './fitness-items-list-presentation.component.scss',
    '../../global.component.scss',
  ],
})
export class FitnessItemsListPresentationMobileComponent extends FitnessItemsListPresentationComponentBase {
  constructor(
    MobilePCDialogService: NbDialogService,
    MobilePCAppRef: ApplicationRef,
    MobilePCChdetRef: ChangeDetectorRef,
    deviceService: DeviceDetectorService
  ) {
    super(
      MobilePCDialogService,
      MobilePCAppRef,
      MobilePCChdetRef,
      true,
      deviceService
    );
  }

  @Input('FitnessBlock') FitnessBlock: FitnessBlock;


}
